import { CategoryButton, ViewStoreButton } from '@/components/feature/floor-guide';
import { PaperIcon } from '@/components/icons/floor-guide';
import { NAV_KEYS } from '@/constants/keyboard';
import { BuildingMenuType } from '@/enums';
import { useAccessibilityTTS } from '@/hooks';
import type { KioskSelectedCategory } from '@/services/dto';
import { useConfigStore } from '@/stores';
import type { GroupedPoiByFloorId } from '@/types/map';
import { filterCategoriesWithMapPoi, getCategoryDisplayName, getCategoryIcon } from '@/utils/floor-guide';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

type Props = {
    groupedPoiByFloorId?: GroupedPoiByFloorId;
    onShowPoiList?: () => void;
    onSelectCategory?: (category: KioskSelectedCategory) => void;
    selectedCategoryCode?: string;
};

export const HorizontalCategoryList = ({
    groupedPoiByFloorId,
    onShowPoiList,
    onSelectCategory,
    selectedCategoryCode,
}: Props) => {
    const { t, i18n } = useTranslation();
    const language = useMemo(() => i18n.language, [i18n]);
    const { speak } = useAccessibilityTTS();

    const selectedCategories = useConfigStore(
        (state) =>
            state.config.server?.buildingMenus?.[language]?.find((menu) => menu.menuType === BuildingMenuType.FLOOR)?.selectedCategories
    );

    const buildingCategories = useMemo(
        () => filterCategoriesWithMapPoi(selectedCategories, groupedPoiByFloorId),
        [selectedCategories, groupedPoiByFloorId]
    );

    const isFullWidth = Array.isArray(buildingCategories) && buildingCategories.length < 10;

    return (
        <section className={styles['horizontal-category__wrapper']}>
            <div className="flex flex-col">
                <ViewStoreButton
                    data-region-nav
                    data-region-order={7}
                    tabIndex={5}
                    aria-label={t('floor_guide.button.view_store_list')}
                    data-interactive
                    data-playsound="ting"
                    data-role={t('common.button.tts.popup_button')}
                    icon={<PaperIcon />}
                    onClick={onShowPoiList}
                >
                    {t('floor_guide.button.view_store_list')}
                </ViewStoreButton>

                <div className="bg-not-contrast-gray-999 h-[calc(1*var(--vh-unit))] w-full" />
            </div>

            <div className={clsx(styles['horizontal-category__list'], 'scrollbar-hide')} data-region-nav data-region-order={8} data-tts-scope="floor-button">
                {Array.isArray(buildingCategories) &&
                    buildingCategories.length > 0 &&
                    buildingCategories?.map((item, index) => {
                        const categoryName = getCategoryDisplayName(item, language);
                        const isSelected =
                            selectedCategoryCode === item.categoryCode ||
                            item.mergeCategoryCode?.includes(selectedCategoryCode || '');

                        return (
                            <div
                                key={`${item.clientCode}-${index}`}
                                className={clsx({
                                    'shrink-0': !isFullWidth,
                                    'flex-1': isFullWidth,
                                })}
                            >
                                <CategoryButton
                                    fullWidth={isFullWidth}
                                    isSelected={isSelected}
                                    tabIndex={6}
                                    onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                                        if (NAV_KEYS.includes(e.key)) {
                                            speak(
                                                t('floor_guide.text_to_speech.category_btn', {
                                                    categoryName,
                                                })
                                            );
                                        }
                                    }}
                                    data-playsound="ting"
                                    data-interactive
                                    icon={getCategoryIcon(item?.clientCode)}
                                    onClick={() => onSelectCategory?.(item)}
                                >
                                    {categoryName}
                                </CategoryButton>
                            </div>
                        );
                    })}
            </div>
        </section>
    );
};
